import { PlusSquareOutlined } from '@ant-design/icons';
import {
  Select,
  type SelectProps,
} from '@framework/ui/components/select/Select.tsx';
import { groupBy, toPairs } from 'lodash';
import { useMemo } from 'react';
import styled from 'styled-components';
import { useSectionType } from '../../sections/SectionTypeContext.tsx';
import { filterWidgetsBySectionType } from '../filterWidgetsBySectionType.ts';
import { getWidgetTypeAliases, type IWidgetType } from '../IWidgetType.ts';
import { WidgetRepository } from '../repository/WidgetRepository.ts';
import { type WidgetType } from '../Widget.ts';
import { WidgetMainTypeOptions } from './WidgetMainTypeOptions.tsx';

export interface WidgetSelectProps extends SelectProps<any> {
  width?: string;
  onChange?: (value: WidgetType) => void;
  /**
   * Filtra i widget da mostrare nella lista
   */
  filter?: (widget: IWidgetType<any>) => boolean;
  /**
   * Tipi di widget aggiuntivi da aggiungere alla lista, a prescindere dal
   * filtro passato in `filter`.
   */
  extraTypes?: WidgetType[];
}

export const CustomOptions = styled.span`
  display: flex;
  align-items: center;
  font-size: 16px;
  .anticon {
    margin-right: 8px;
    font-size: 20px;
  }
`;

const CustomOptionsDivider = styled.div`
  padding-top: 12px;
  font-weight: 400;
  font-size: 14px;
`;

/**
 * Select per selezionare un tipo di widget
 */
export function WidgetSelect(props: WidgetSelectProps) {
  const { width, onChange, filter, extraTypes, ...otherProps } = props;
  const { sectionType } = useSectionType();

  const options = useMemo(() => {
    const defaultFilter = (w: IWidgetType<any>) =>
      filterWidgetsBySectionType(w, sectionType);

    const widgets = [
      // 1. Quelli filtrati
      ...WidgetRepository.listAll().filter(filter ?? defaultFilter),
      // 2. Aggiungiamo gli extra a prescindere dal filtro
      ...(extraTypes?.map(t => WidgetRepository.find(t)!) ?? []),
    ];

    const widgetTypes = groupBy(widgets, t => t.category);

    return toPairs(widgetTypes)
      .sort((w1, w2) => w2[0].localeCompare(w1[0]))
      .map(([group, types]) => ({
        label: (
          <CustomOptionsDivider>
            {WidgetMainTypeOptions[group] || 'Altro'}
          </CustomOptionsDivider>
        ),
        options: types.map(w => ({
          label: (
            <CustomOptions>
              {w.icon ?? <PlusSquareOutlined />} {w.name ?? w.type}
            </CustomOptions>
          ),
          labelText: w.name ?? w.type,
          value: w.type,
          aliases: getWidgetTypeAliases(w),
        })),
      }));
  }, [filter, extraTypes, sectionType]);

  return (
    <Select
      style={{ width: `${width ?? '100%'}` }}
      placeholder="Seleziona un tipo di campo per aggiungerlo"
      showSearch
      filterOption={(input, option) => {
        return option?.aliases?.some((alias: string) =>
          alias.toLowerCase().includes(input.toLowerCase()),
        );
      }}
      size="large"
      options={options}
      // Il value è null perché il componente non seleziona davvero un widget
      // ma è utilizzato unicamente come supporto grafico alla funzione di onChange
      // value={null}
      {...otherProps}
      onChange={onChange}
    />
  );
}
